import { useState, useEffect } from 'react';
import * as directorySubmissionService from '../../../services/directorySubmissionService';
import * as profileListingService from '../../../services/profileListingService';
import type { DirectorySubmissionModuleData, DirectorySubmissionItem } from '../../../services/directorySubmissionService';

interface DirectorySubmissionDetailProps {
  onBack: () => void;
  darkMode: boolean;
}

interface NapIssue {
  id: string;
  directory: string;
  field: string;
  current: string;
  expected: string;
  severity: 'critical' | 'warning' | 'info';
}

export default function DirectorySubmissionDetail({ onBack, darkMode }: DirectorySubmissionDetailProps) {
  const [activeTab, setActiveTab] = useState<'new' | 'existing'>('new');
  
  // API State
  const [moduleData, setModuleData] = useState<DirectorySubmissionModuleData | null>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationId, setGenerationId] = useState<string | null>(null);
  const [generatedListings, setGeneratedListings] = useState<DirectorySubmissionItem[]>([]);
  const [directoryData, setDirectoryData] = useState<DirectorySubmissionItem | null>(null);
  const [listingsGenerated, setListingsGenerated] = useState(false);
  const [drawerListing, setDrawerListing] = useState<DirectorySubmissionItem | null>(null);
  const [drawerMode, setDrawerMode] = useState<'view' | 'edit'>('view');
  const [editShortDesc, setEditShortDesc] = useState('');
  const [editLongDesc, setEditLongDesc] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editTags, setEditTags] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const openDrawer = (listing: DirectorySubmissionItem) => {
    setDrawerListing(listing);
    setDrawerMode('view');
    setEditShortDesc(listing.short_description || '');
    setEditLongDesc(listing.long_description || '');
    setEditCategory(listing.category || listing.category || '');
    setEditTags((listing.tags || []).join(', '));
  };

  const closeDrawer = () => {
    setDrawerListing(null);
    setDrawerMode('view');
    setEditShortDesc('');
    setEditLongDesc('');
    setEditCategory('');
    setEditTags('');
  };

  const saveFromDrawer = async () => {
    if (!drawerListing) return;
    setIsSavingEdit(true);
    try {
      const parsedTags = editTags
        .split(',')
        .map(t => t.trim())
        .filter(t => t.length > 0);

      const updated = await directorySubmissionService.updateListing(drawerListing.id, {
        short_description: editShortDesc,
        long_description: editLongDesc,
        category: editCategory,
        tags: parsedTags,
      });

      setGeneratedListings(prev =>
        prev.map(item => (item.id === drawerListing.id ? updated : item))
      );
      setDrawerListing(updated);
      setDrawerMode('view');
    } catch (err) {
      console.error('Failed to save directory submission edits:', err);
    } finally {
      setIsSavingEdit(false);
    }
  };
  const [dirCopiedField, setDirCopiedField] = useState<string | null>(null);
  
  // UI State
  const [showOutput, setShowOutput] = useState(false);

  // Audit / NAP scan state
  const [isScanning, setIsScanning] = useState(false);
  const [scanId, setScanId] = useState<string | null>(null);
  const [scanAssets, setScanAssets] = useState<any[]>([]);
  const [napAuditRun, setNapAuditRun] = useState(false);

  // Active Selection & Asset Checklist State
  const [selectedListing, setSelectedListing] = useState<DirectorySubmissionItem | null>(null);
  const [checkedPlatforms, setCheckedPlatforms] = useState<Record<string, boolean>>({});
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);
  const [markCompleteSuccess, setMarkCompleteSuccess] = useState(false);


  // Sync directoryData with generatedListings
  useEffect(() => {
    if (generatedListings.length > 0) {
      const valid = generatedListings.find(l => l.status !== 'FAILED');
      setDirectoryData(valid || generatedListings[0]);
    } else {
      setDirectoryData(null);
    }
  }, [generatedListings]);

  // Sync checkedPlatforms state with directoryData's published_platforms
  useEffect(() => {
    if (directoryData && moduleData?.platforms) {
      const initialChecked: Record<string, boolean> = {};
      const published = directoryData.published_platforms || [];
      moduleData.platforms.forEach(p => {
        initialChecked[p.platform_name] = published.includes(p.platform_name);
      });
      setCheckedPlatforms(initialChecked);
    } else {
      setCheckedPlatforms({});
    }
  }, [directoryData, moduleData]);

  const handleTogglePlatformCheckbox = (platformName: string) => {
    setCheckedPlatforms(prev => ({
      ...prev,
      [platformName]: !prev[platformName]
    }));
  };

  const handleMarkCompletePerListing = async () => {
    if (!directoryData) return;
    setIsMarkingComplete(true);
    try {
      const selectedPlatforms = Object.keys(checkedPlatforms).filter(p => checkedPlatforms[p]);
      const updated = await directorySubmissionService.updateListing(directoryData.id, {
        published_platforms: selectedPlatforms
      });
      
      setGeneratedListings(prev => prev.map(l => l.id === updated.id ? updated : l));
      setDirectoryData(updated);
      setMarkCompleteSuccess(true);
      setTimeout(() => setMarkCompleteSuccess(false), 3000);
    } catch (err) {
      console.error('Error marking complete for directory listing:', err);
    } finally {
      setIsMarkingComplete(false);
    }
  };

  const cardBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500';

  useEffect(() => {
    async function loadData() {
      try {
        const data = await directorySubmissionService.getModuleData();
        setModuleData(data);
        if (data.is_new_website) {
          setActiveTab('new');
        } else {
          setActiveTab('existing');
        }
        
        // Also fetch any previously generated listings
        const existingListings = await directorySubmissionService.getAllListings();
        if (existingListings.length > 0) {
          setGeneratedListings(existingListings);
          setListingsGenerated(true);
          setNapAuditRun(true);
          setShowOutput(true);
        }
      } catch (err) {
        console.error('Failed to load directory submission module data', err);
      } finally {
        setLoadingData(false);
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    let interval: number | undefined;
    if (generationId && isGenerating) {
      interval = window.setInterval(async () => {
        try {
          const items = await directorySubmissionService.getGeneratedListings(generationId);
          setGeneratedListings(items);
          const allDone = items.length > 0 && items.every(item => item.status !== 'GENERATING');
          if (allDone) {
            setIsGenerating(false);
            setListingsGenerated(true);
          }
        } catch (err) {
          console.error('Error polling listings', err);
        }
      }, 3000);
    }
    return () => {
      if (interval) window.clearInterval(interval);
    };
  }, [generationId, isGenerating]);

  const generateDirectories = async () => {
    if (!moduleData) return;
    setIsGenerating(true);
    setListingsGenerated(false);
    
    try {
      // Generate only for top 6 platforms as requested
      const platformNames = moduleData.platforms
        .sort((a, b) => b.da_score - a.da_score)
        .slice(0, 6)
        .map(p => p.platform_name);
      
      const res = await directorySubmissionService.generateContent({ platform_names: platformNames });
      setGenerationId(res.generation_id);
    } catch (err) {
      console.error('Error triggering generation', err);
      setIsGenerating(false);
    }
  };

  const handleGenerateClick = () => {
    if (!showOutput && listingsGenerated) {
      // Just expand if we already have generated listings
      setShowOutput(true);
    } else {
      // Generate and expand
      setShowOutput(true);
      generateDirectories();
    }
  };

  const getPriorityColor = (da: number) => {
    if (da >= 80) return darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700';
    if (da >= 60) return darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-500';
    return darkMode ? 'bg-gray-800 text-gray-500' : 'bg-gray-50 text-gray-400';
  };

  const getPriorityLabel = (da: number) => {
    if (da >= 80) return 'high';
    if (da >= 60) return 'medium';
    return 'low';
  };

  // Polling for Scan
  useEffect(() => {
    let interval: number | undefined;
    if (scanId && isScanning) {
      interval = window.setInterval(async () => {
        try {
          const res = await profileListingService.getScanStatus(scanId);
          if (res.status === 'COMPLETED') {
            setIsScanning(false);
            setScanAssets(res.result?.assets || []);
            setNapAuditRun(true);
          } else if (res.status === 'FAILED') {
            setIsScanning(false);
            setNapAuditRun(true);
            setScanAssets([]);
          }
        } catch (err) {
          console.error('Error polling scan status', err);
        }
      }, 3000);
    }
    return () => {
      if (interval) window.clearInterval(interval);
    };
  }, [scanId, isScanning]);

  const runNapAudit = async () => {
    setIsScanning(true);
    setNapAuditRun(false); // Reset before new scan
    try {
      const userId = localStorage.getItem('user_id');
      if (!userId) {
        console.error('No user ID found for audit');
        setIsScanning(false);
        return;
      }
      const res = await profileListingService.initiateScan(userId, 'directory_submission');
      setScanId(res.scan_id);
    } catch (err) {
      console.error('Failed to initiate scan', err);
      setIsScanning(false);
    }
  };

  const fallbackCopy = (text: string) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.position = "fixed";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try { document.execCommand("copy"); } catch (e) {}
    document.body.removeChild(textArea);
  };

  const handleDirCopyField = (text: string, field: string) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
    } else {
      fallbackCopy(text);
    }
    setDirCopiedField(field);
    setTimeout(() => setDirCopiedField(null), 2000);
  };

  const handleDirCopyAll = (listing: DirectorySubmissionItem) => {
    const text = [
      `Short Description: ${listing.short_description || ''}`,
      `Long Description: ${listing.long_description || ''}`,
      `Category: ${napPreview.category || ''}`,
      `Tags: ${(listing.tags || []).join(', ')}`,
    ].join('\n\n');
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
    } else {
      fallbackCopy(text);
    }
    setDirCopiedField('all');
    setTimeout(() => setDirCopiedField(null), 2000);
  };

  if (loadingData) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Use only Top 6 platforms
  const platforms = (moduleData?.platforms || []).sort((a, b) => b.da_score - a.da_score).slice(0, 6);
  const project_context = moduleData?.project_context;
  
  const napPreview = {
    name: project_context?.brand || 'Not set',
    description: project_context?.description || 'Not set',
    descriptionShort: project_context?.description ? project_context.description.substring(0, 150) + '...' : 'Not set',
    url: project_context?.website_url || 'Not set',
    phone: project_context?.phone || 'Not set',
    address: project_context?.address || 'Not set',
    category: project_context?.primary_business || 'Business',
    hours: project_context?.operating_hours || 'Not set',
  };

  const highPriorityCount = platforms.filter(p => p.da_score >= 80).length;
  const medPriorityCount = platforms.filter(p => p.da_score >= 60 && p.da_score < 80).length;
  const lowPriorityCount = platforms.filter(p => p.da_score < 60).length;

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="flex items-center space-x-2 text-sm font-medium text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          <span>Back to Off-Page Content</span>
        </button>
      </div>

      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0 border border-blue-200 dark:border-blue-800">
          <i className="ri-folder-open-line text-blue-600 dark:text-blue-400 text-xl"></i>
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            Directory Submission
            <div className="relative group cursor-pointer inline-flex items-center z-40 ml-2">
              <i
                className="ri-information-line text-lg text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  const t = document.getElementById('directory-submission-tooltip');
                  if (t) {
                    t.classList.toggle('opacity-0');
                    t.classList.toggle('opacity-100');
                    setTimeout(() => {
                      t.classList.add('opacity-0');
                      t.classList.remove('opacity-100');
                    }, 4000);
                  }
                }}
              ></i>
              <div
                id="directory-submission-tooltip"
                className="absolute left-0 top-full mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-left p-4 rounded-xl shadow-xl opacity-0 pointer-events-none transition-opacity z-50 leading-relaxed"
              >
                <p className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <i className="ri-error-warning-line text-blue-600 dark:text-blue-400"></i>
                  Prerequisite Inputs
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5"></i>
                    <span>Business NAP data (Name, Address, Phone)</span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5"></i>
                    <span>Business category</span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5"></i>
                    <span>Website URL</span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5"></i>
                    <span>Short &amp; long brand descriptions</span>
                  </li>
                </ul>
              </div>
            </div>
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">List your business on relevant industry and geographic directories.</p>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="inline-flex bg-gray-100 dark:bg-gray-800 rounded-full p-1">
        <button
          onClick={() => setActiveTab('new')}
          disabled={!moduleData?.is_new_website}
          className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
            !moduleData?.is_new_website ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          } ${
            activeTab === 'new'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <i className="ri-add-circle-line mr-1.5"></i>
          New Website
        </button>
        <button
          onClick={() => setActiveTab('existing')}
          disabled={moduleData?.is_new_website}
          className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
            moduleData?.is_new_website ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          } ${
            activeTab === 'existing'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <i className="ri-refresh-line mr-1.5"></i>
          Existing Website
        </button>
      </div>

      {/* Audit Section for Existing Website Only */}
      {activeTab === 'existing' && (
        <div className={`${cardBg} rounded-xl border ${borderColor} p-5`}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <div>
              <h3 className={`text-sm font-semibold ${textPrimary} flex items-center gap-2`}>
                <i className="ri-radar-line text-blue-600 dark:text-blue-400"></i>
                Directory Inconsistency Scanner
              </h3>
              <p className={`text-xs ${textSecondary} mt-0.5`}>
                Scans top 50 global directories for missing profiles and NAP (Name, Address, Phone) inconsistencies.
              </p>
            </div>
            
            {!napAuditRun && !isScanning ? (
              <button
                onClick={runNapAudit}
                className="px-5 py-2.5 bg-white border border-gray-200 dark:bg-gray-800 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-search-eye-line mr-2"></i>
                Run NAP Audit
              </button>
            ) : isScanning ? (
              <button disabled className="px-5 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-sm font-medium rounded-lg opacity-70 flex items-center cursor-not-allowed">
                <i className="ri-loader-4-line animate-spin mr-2"></i>
                Scanning...
              </button>
            ) : (
              <div className="flex items-center gap-3">
                <span className="px-3 py-1.5 text-xs font-medium rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                  <i className="ri-check-line mr-1"></i>
                  Scan Completed
                </span>
                <button
                  onClick={runNapAudit}
                  className="px-4 py-1.5 text-xs font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer bg-white dark:bg-gray-800 border border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                >
                  <i className="ri-refresh-line mr-1"></i>Re-scan
                </button>
              </div>
            )}
          </div>

          {napAuditRun && (
            <div className="space-y-4">
              {scanAssets.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 mt-4 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                  <div className="w-12 h-12 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mb-3">
                    <i className="ri-information-line text-blue-600 dark:text-blue-400 text-xl"></i>
                  </div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">No Existing Listings Found</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center max-w-md">
                    We scanned the target directories but couldn't find any existing business profiles matching your brand and website.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Directory</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Field</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Current Value</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Expected Value</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Severity</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {scanAssets.map((issue, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                        <td className="py-3 px-4">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">{issue.directory}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{issue.field}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-red-600 dark:text-red-400 line-through">{issue.current}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-emerald-600 dark:text-emerald-400">{issue.expected}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            issue.severity === 'critical' 
                              ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400' 
                              : 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                          }`}>
                            {issue.severity.charAt(0).toUpperCase() + issue.severity.slice(1)}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <button className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline">
                            Correct Profile
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Pre-Filled Data Preview */}
      <div className={`${cardBg} rounded-xl border ${borderColor} p-5`}>
        <h3 className={`text-sm font-semibold ${textPrimary} mb-4 flex items-center gap-2`}>
          <i className="ri-draft-line text-blue-600 dark:text-blue-400"></i>
          Pre-Filled Submission Data
          <div className="relative group cursor-pointer inline-flex items-center z-40 ml-1">
              <i className="ri-question-line text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"></i>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded-lg p-2.5 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity text-center font-normal leading-relaxed shadow-lg">
                You can enter or update these details in the Profile section.
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-200 dark:border-t-gray-700"></div>
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-white dark:border-t-gray-800" style={{ marginTop: '-1px' }}></div>
              </div>
          </div>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <span className="text-xs text-gray-400 dark:text-gray-500">Business Name</span>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{napPreview.name}</p>
            </div>
            <div>
              <span className="text-xs text-gray-400 dark:text-gray-500">Phone</span>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{napPreview.phone}</p>
            </div>
            <div>
              <span className="text-xs text-gray-400 dark:text-gray-500">Address</span>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{napPreview.address}</p>
            </div>
            <div>
              <span className="text-xs text-gray-400 dark:text-gray-500">Website</span>
              <p className="text-sm font-medium text-blue-600 dark:text-blue-400">{napPreview.url}</p>
            </div>
            <div>
              <span className="text-xs text-gray-400 dark:text-gray-500">Category &amp; Hours</span>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{napPreview.category}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{napPreview.hours}</p>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <span className="text-xs text-gray-400 dark:text-gray-500">Short Description (for listings with character limits)</span>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">{napPreview.descriptionShort}</p>
            </div>
            <div>
              <span className="text-xs text-gray-400 dark:text-gray-500">Full Description (200–500 words)</span>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">{napPreview.description}</p>
            </div>
          </div>
        </div>
      </div>
      {/* Output / Generated Assets */}
      <div className={`${cardBg} rounded-xl border ${borderColor} p-5 space-y-6`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className={`text-sm font-semibold ${textPrimary}`}>Generated Directory List</h3>
            <p className={`text-xs ${textSecondary} mt-0.5`}>
              Click a directory asset to view details and track submission checklists below
            </p>
          </div>
          <div className="flex items-center gap-3">
            {!isGenerating ? (
              <button
                onClick={handleGenerateClick}
                className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className={(listingsGenerated && showOutput) ? "ri-refresh-line mr-2" : "ri-magic-line mr-2"}></i>
                {(listingsGenerated && showOutput) ? "Regenerate" : "Generate Directory List"}
              </button>
            ) : (
              <button disabled className="px-5 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-sm font-medium rounded-lg opacity-70 flex items-center cursor-not-allowed">
                <i className="ri-loader-4-line animate-spin mr-2"></i>
                Generating...
              </button>
            )}
          </div>
        </div>

        {showOutput && (
          <>
            {isGenerating && !directoryData && (
              <div className="flex flex-col items-center justify-center p-8 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                <p className="text-sm text-gray-500 dark:text-gray-400">AI is processing directory submissions...</p>
              </div>
            )}

            {directoryData && (
              <div className="space-y-6">
                {/* Full-Width Asset Card */}
                <div className="bg-gray-50 dark:bg-gray-800/60 rounded-xl p-5 border border-gray-200 dark:border-gray-700 space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <h4 className="font-bold text-sm text-gray-900 dark:text-white flex items-center gap-2">
                      <i className="ri-folder-info-line text-blue-500"></i>
                      Directory Submission Details
                    </h4>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openDrawer(directoryData)}
                        className="px-3 py-1.5 bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 text-xs font-bold rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 cursor-pointer flex items-center gap-1.5 transition-colors"
                      >
                        <i className="ri-pencil-line" /> Edit Details
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                    <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                      <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider block mb-1">Category</span>
                      <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm truncate">{directoryData.category || napPreview.category}</p>
                    </div>
                    <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                      <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider block mb-1">Website</span>
                      <p className="font-semibold text-blue-600 dark:text-blue-400 text-sm truncate">{directoryData.website_url || napPreview.url}</p>
                    </div>
                    
                    <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 md:col-span-2">
                      <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider block mb-1">Short Description</span>
                      <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">{directoryData.short_description || napPreview.descriptionShort}</p>
                    </div>

                    {directoryData.long_description && (
                      <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 md:col-span-2">
                        <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider block mb-1">Full Description</span>
                        <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">{directoryData.long_description}</p>
                      </div>
                    )}
                    
                    {directoryData.tags && directoryData.tags.length > 0 && (
                      <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 md:col-span-2">
                        <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider block mb-1">Tags</span>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {directoryData.tags.map(t => (
                            <span key={t} className="px-2 py-1 text-[11px] font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-md">
                              #{t}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Submission Checklist Panel */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700 space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <h4 className="font-bold text-sm text-gray-900 dark:text-white flex items-center gap-2">
                        <i className="ri-global-line text-blue-500" /> Target Directories Checklist
                      </h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Check off the directories as you submit this listing, then save your progress.
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      {markCompleteSuccess && (
                        <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                          <i className="ri-checkbox-circle-fill" /> Saved!
                        </span>
                      )}
                      <button
                        onClick={handleMarkCompletePerListing}
                        disabled={isMarkingComplete}
                        className="px-5 py-2.5 text-xs font-bold rounded-xl bg-blue-600 hover:bg-blue-700 text-white transition-all shadow-sm cursor-pointer border-none flex items-center gap-1.5"
                      >
                        {isMarkingComplete ? (
                          <><i className="ri-loader-4-line animate-spin" /> Saving...</>
                        ) : (
                          <><i className="ri-checkbox-circle-line" /> Save Progress</>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3 mt-4">
                    {(platforms || [])
                      .slice()
                      .sort((a, b) => b.da_score - a.da_score)
                      .map((platform) => {
                      const isChecked = !!checkedPlatforms[platform.platform_name];

                      return (
                        <div
                          key={platform.platform_name}
                          className={`flex items-center justify-between p-4 rounded-xl border transition-all ${
                            isChecked
                              ? 'bg-emerald-50/30 dark:bg-emerald-950/10 border-emerald-200 dark:border-emerald-900/50'
                              : 'bg-gray-50 dark:bg-gray-700/30 border-gray-100 dark:border-gray-700'
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleTogglePlatformCheckbox(platform.platform_name)}
                              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                            />
                            <div className="w-10 h-10 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 flex items-center justify-center">
                              <i className={`ri-global-line ${isChecked ? 'text-emerald-500' : 'text-gray-400'} text-xl`}></i>
                            </div>
                            <div>
                              <span className={`text-sm font-bold block ${isChecked ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'}`}>
                                {platform.platform_name}
                              </span>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">
                                  DA {platform.da_score}
                                </span>
                                {isChecked && (
                                  <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-0.5">
                                    <i className="ri-check-line" /> Completed
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          <a
                            href={`https://${platform.platform_url || platform.platform_name.toLowerCase().replace(/\s+/g, '') + '.com'}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs text-gray-700 dark:text-gray-300 hover:text-blue-600 hover:border-blue-300 dark:hover:text-blue-400 transition-colors rounded-lg flex items-center gap-1.5 font-semibold"
                          >
                            Visit Site <i className="ri-external-link-line" />
                          </a>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail Drawer */}
      {drawerListing && (
        <>
          <div 
            className="fixed inset-0 bg-black/40 dark:bg-black/60 z-40 backdrop-blur-sm transition-opacity"
            onClick={closeDrawer}
          />
          <div className={`fixed inset-y-0 right-0 w-[500px] ${cardBg} shadow-2xl z-50 transform transition-transform duration-300 flex flex-col border-l ${borderColor} overflow-hidden`}>
            <div className={`px-6 py-4 border-b ${borderColor} flex items-center justify-between`}>
              <div>
                <h2 className={`text-lg font-bold ${textPrimary}`}>
                  {drawerMode === 'edit' ? 'Edit Directory Profile' : 'Directory Profile'}
                </h2>
                <p className={`text-sm ${textSecondary}`}>
                  {drawerMode === 'edit' ? 'Modify generated profile details.' : 'Review generated profile details.'}
                </p>
              </div>
              <button 
                onClick={closeDrawer}
                className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 ${textSecondary} transition-colors cursor-pointer`}
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              <div className="space-y-4">
                {drawerMode === 'view' ? (
                  <>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <h4 className={`text-xs font-semibold uppercase tracking-wider ${textSecondary}`}>Short Description</h4>
                        <button onClick={() => handleDirCopyField(drawerListing.short_description || '', 'short')} className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1 cursor-pointer bg-transparent border-none font-semibold hover:underline">
                          <i className={dirCopiedField === 'short' ? 'ri-check-line text-emerald-500' : 'ri-file-copy-line'} />
                          {dirCopiedField === 'short' ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                      <p className={`text-sm ${textPrimary} whitespace-pre-wrap leading-relaxed`}>{drawerListing.short_description || <span className="text-gray-400 italic">Not set</span>}</p>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <h4 className={`text-xs font-semibold uppercase tracking-wider ${textSecondary}`}>Long Description</h4>
                        <button onClick={() => handleDirCopyField(drawerListing.long_description || '', 'long')} className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1 cursor-pointer bg-transparent border-none font-semibold hover:underline">
                          <i className={dirCopiedField === 'long' ? 'ri-check-line text-emerald-500' : 'ri-file-copy-line'} />
                          {dirCopiedField === 'long' ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                      <p className={`text-sm ${textPrimary} whitespace-pre-wrap leading-relaxed`}>{drawerListing.long_description || <span className="text-gray-400 italic">Not set</span>}</p>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <h4 className={`text-xs font-semibold uppercase tracking-wider ${textSecondary}`}>Category</h4>
                        <button onClick={() => handleDirCopyField(drawerListing.category || napPreview.category || '', 'category')} className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1 cursor-pointer bg-transparent border-none font-semibold hover:underline">
                          <i className={dirCopiedField === 'category' ? 'ri-check-line text-emerald-500' : 'ri-file-copy-line'} />
                          {dirCopiedField === 'category' ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                      <p className={`text-sm ${textPrimary}`}>{drawerListing.category || napPreview.category || <span className="text-gray-400 italic">Not set</span>}</p>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <h4 className={`text-xs font-semibold uppercase tracking-wider ${textSecondary}`}>Tags</h4>
                        <button onClick={() => handleDirCopyField((drawerListing.tags || []).join(', '), 'tags')} className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1 cursor-pointer bg-transparent border-none font-semibold hover:underline">
                          <i className={dirCopiedField === 'tags' ? 'ri-check-line text-emerald-500' : 'ri-file-copy-line'} />
                          {dirCopiedField === 'tags' ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {drawerListing.tags && drawerListing.tags.length > 0 ? (
                          drawerListing.tags.map((tag, idx) => (
                            <span key={idx} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded-md">
                              {tag}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm text-gray-400 italic">No tags generated</span>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <h4 className={`text-xs font-semibold uppercase tracking-wider ${textSecondary} mb-1.5`}>Short Description</h4>
                      <textarea
                        rows={3}
                        value={editShortDesc}
                        onChange={(e) => setEditShortDesc(e.target.value)}
                        className="w-full text-sm p-2.5 border rounded-lg border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                        placeholder="Enter short description..."
                      />
                    </div>
                    <div>
                      <h4 className={`text-xs font-semibold uppercase tracking-wider ${textSecondary} mb-1.5`}>Long Description</h4>
                      <textarea
                        rows={6}
                        value={editLongDesc}
                        onChange={(e) => setEditLongDesc(e.target.value)}
                        className="w-full text-sm p-2.5 border rounded-lg border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                        placeholder="Enter long description..."
                      />
                    </div>
                    <div>
                      <h4 className={`text-xs font-semibold uppercase tracking-wider ${textSecondary} mb-1.5`}>Category</h4>
                      <input
                        type="text"
                        value={editCategory}
                        onChange={(e) => setEditCategory(e.target.value)}
                        className="w-full text-sm p-2.5 border rounded-lg border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        placeholder="Enter category..."
                      />
                    </div>
                    <div>
                      <h4 className={`text-xs font-semibold uppercase tracking-wider ${textSecondary} mb-1.5`}>Tags</h4>
                      <input
                        type="text"
                        value={editTags}
                        onChange={(e) => setEditTags(e.target.value)}
                        className="w-full text-sm p-2.5 border rounded-lg border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        placeholder="tag1, tag2, tag3"
                      />
                      <p className="text-[10px] text-gray-400 mt-1">Separate tags with commas</p>
                    </div>
                  </>
                )}
              </div>
            </div>
            {/* Drawer Footer with Copy All / Edit / Save / Cancel */}
            <div className={`px-6 py-4 border-t ${borderColor} flex justify-between items-center bg-gray-50 dark:bg-gray-800/80`}>
              {drawerMode === 'view' ? (
                <>
                  <button
                    onClick={() => handleDirCopyAll(drawerListing)}
                    className={`flex items-center gap-1.5 text-sm px-4 py-2 border rounded-lg transition-colors cursor-pointer ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                  >
                    <i className={dirCopiedField === 'all' ? 'ri-check-double-line text-emerald-500' : 'ri-file-copy-line'} />
                    {dirCopiedField === 'all' ? 'All Copied!' : 'Copy All'}
                  </button>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={closeDrawer}
                      className={`text-sm px-4 py-2 transition-colors cursor-pointer ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}
                    >
                      Close
                    </button>
                    <button
                      onClick={() => setDrawerMode('edit')}
                      className="text-sm px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors cursor-pointer flex items-center gap-1.5 font-medium"
                    >
                      <i className="ri-pencil-line" /> Edit Profile
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setDrawerMode('view')}
                    className={`text-sm px-4 py-2 transition-colors cursor-pointer ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveFromDrawer}
                    disabled={isSavingEdit}
                    className="text-sm px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors cursor-pointer flex items-center gap-1.5 font-medium disabled:opacity-50"
                  >
                    {isSavingEdit ? (
                      <><i className="ri-loader-4-line animate-spin" /> Saving...</>
                    ) : (
                      <><i className="ri-save-line" /> Save Changes</>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}

    </div>
  );
}
